// src/ui/ChatAvatar.jsx — the avatar for a CHAT, wherever a chat is listed.
//
// 🟥 WHY THIS EXISTS. <Avatar chat={c} /> alone cannot draw a 1:1 photo: for a
//    direct chat it resolves the picture from the peer's profile, or failing
//    that from chat.participantImages[peerUid] — and it can only do the second
//    if you tell it WHO the peer is. Passing just the chat (as the list editor
//    and the search results did) left both routes empty, so every person fell
//    back to an initial while groups looked fine.
//
//    Rather than repeating the peerUid + profile-fetch dance at each call site
//    — which is exactly how it got missed twice — it lives here once.

import React, { useEffect, useState } from 'react';
import Avatar, { resolveName, groupTitle } from './Avatar';
import { otherParticipant, isNoteToSelf } from '../services/chats';
import { getUserProfile } from '../services/users';

/** The peer's profile for a 1:1 chat (null for groups). */
export function useChatPeer(chat, myUid) {
  const peerUid = chat?.isGroup ? null : otherParticipant(chat, myUid);
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    let live = true;
    if (!peerUid) { setProfile(null); return undefined; }
    getUserProfile(peerUid).then((p) => { if (live) setProfile(p); }).catch(() => {});
    return () => { live = false; };
  }, [peerUid]);
  return { peerUid, profile };
}

/** The chat's display name — same fallback order the sidebar rows use, so a 1:1
 *  whose participantNames map is empty still shows a real name once the peer's
 *  profile lands rather than a permanent "Allo user". */
export function ChatTitle({ chat, myUid }) {
  const { peerUid, profile } = useChatPeer(chat, myUid);
  if (chat?.isGroup) return <>{groupTitle(chat)}</>;
  if (isNoteToSelf(chat, myUid)) return <>Note to Self</>;
  const fromProfile = resolveName(profile);
  return <>{fromProfile !== 'Allo user' ? fromProfile : (chat?.participantNames?.[peerUid] || 'Allo user')}</>;
}

export default function ChatAvatar({ chat, myUid, size = 'sm', online }) {
  const { peerUid, profile } = useChatPeer(chat, myUid);

  return (
    <Avatar
      chat={chat}
      profile={profile}
      peerUid={peerUid}
      myUid={myUid}
      size={size}
      online={online}
    />
  );
}
